import React, { useState } from 'react';
import {
  LayoutDashboard,
  Globe,
  Image as ImageIcon,
  Mail,
  Sparkles,
  Music,
  Sun,
  Flower2,
  Users,
  Compass,
  ShieldCheck,
  Database,
  ExternalLink,
  LogOut,
  ArrowLeft,
  Copy,
  Menu,
  X,
  Lock,
  Smartphone,
  Wrench,
  Droplets,
  Quote,
  Check,
  BarChart3,
} from 'lucide-react';
import { AdminSection, ADMIN_SECTION_TITLES, ADMIN_SECTION_PATHS } from '../AdminPage';
import { CoupleSettings, VisitorLog, MemoryItem, DailyLetter, MyWebRequest } from '../../types';

interface ModernAdminNavProps {
  activeSection: AdminSection;
  onSelectSection: (section: AdminSection) => void;
  settings: CoupleSettings;
  activeProfile: 'novio' | 'novia' | 'admin';
  p1Name: string;
  p2Name: string;
  p1Avatar: string;
  p2Avatar: string;
  visitorLogs: VisitorLog[];
  memories: MemoryItem[];
  lettersList: DailyLetter[];
  webRequests: MyWebRequest[];
  toast?: string;
  onCloseToast?: () => void;
  onNavigateToPublic: (route?: string) => void;
  onLogout: () => void;
  copyRouteLink: (path: string) => void;
  isUserWeb?: boolean;
  userWebOrderCode?: string;
}

interface NavItemConfig {
  id: AdminSection;
  label: string;
  icon: React.ReactNode;
  category: 'core' | 'content' | 'garden' | 'settings';
}

export const ModernAdminNav: React.FC<ModernAdminNavProps> = ({
  activeSection,
  onSelectSection,
  settings,
  activeProfile,
  p1Name,
  p2Name,
  p1Avatar,
  p2Avatar,
  visitorLogs,
  memories,
  lettersList,
  webRequests,
  toast,
  onCloseToast,
  onNavigateToPublic,
  onLogout,
  copyRouteLink,
  isUserWeb,
  userWebOrderCode,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const currentProfileName =
    activeProfile === 'novio'
      ? p1Name || settings.partner1Name || 'Novio'
      : p2Name || settings.partner2Name || 'Novia';

  const currentProfileAvatar =
    activeProfile === 'novio'
      ? p1Avatar || settings.partner1Avatar
      : p2Avatar || settings.partner2Avatar;

  const navItems: NavItemConfig[] = [
    {
      id: 'dashboard',
      label: 'Panel General',
      icon: <LayoutDashboard className="w-3.5 h-3.5" />,
      category: 'core',
    },
    {
      id: 'memories',
      label: 'Recuerdos',
      icon: <ImageIcon className="w-3.5 h-3.5" />,
      category: 'content',
    },
    {
      id: 'letters',
      label: 'Cartas',
      icon: <Mail className="w-3.5 h-3.5" />,
      category: 'content',
    },
    {
      id: 'couple',
      label: 'Pareja',
      icon: <Users className="w-3.5 h-3.5" />,
      category: 'content',
    },
    {
      id: 'music',
      label: 'Música',
      icon: <Music className="w-3.5 h-3.5" />,
      category: 'content',
    },
    {
      id: 'phrases',
      label: 'Frases',
      icon: <Quote className="w-3.5 h-3.5" />,
      category: 'content',
    },
    {
      id: 'tulips',
      label: 'Tulipanes',
      icon: <Flower2 className="w-3.5 h-3.5" />,
      category: 'garden',
    },
    {
      id: 'watering',
      label: 'Riego',
      icon: <Droplets className="w-3.5 h-3.5" />,
      category: 'garden',
    },
    {
      id: 'atmosphere',
      label: 'Ambiente',
      icon: <Sun className="w-3.5 h-3.5" />,
      category: 'garden',
    },
    {
      id: 'visitors',
      label: 'Estadísticas',
      icon: <BarChart3 className="w-3.5 h-3.5" />,
      category: 'settings',
    },
    {
      id: 'app',
      label: 'App Móvil',
      icon: <Smartphone className="w-3.5 h-3.5" />,
      category: 'settings',
    },
    {
      id: 'web_settings',
      label: 'Ajustes Web',
      icon: <Wrench className="w-3.5 h-3.5" />,
      category: 'settings',
    },
    {
      id: 'myweb',
      label: 'Solicitudes Web',
      icon: <Sparkles className="w-3.5 h-3.5" />,
      category: 'settings',
    },
    {
      id: 'security',
      label: 'Seguridad',
      icon: <ShieldCheck className="w-3.5 h-3.5" />,
      category: 'settings',
    },
    {
      id: 'database',
      label: 'Datos',
      icon: <Database className="w-3.5 h-3.5" />,
      category: 'settings',
    },
    {
      id: 'navigation',
      label: 'Navegación',
      icon: <Compass className="w-3.5 h-3.5" />,
      category: 'settings',
    },
  ];

  // In personalized custom webs, platform and security navigation items are strictly excluded
  const displayedNavItems = isUserWeb
    ? navItems.filter((item) => item.category !== 'settings')
    : navItems;

  const handleNavClick = (sectionId: AdminSection) => {
    onSelectSection(sectionId);
    setMobileMenuOpen(false);
  };

  return (
    <>
      {/* Top Navbar */}
      <header
        id="modern-admin-header"
        className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-slate-200/80 shadow-xs"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          {/* Left: Brand or Breadcrumb */}
          <div className="flex items-center gap-3 min-w-0">
            {activeSection === 'dashboard' ? (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-slate-900 text-white flex items-center justify-center font-black shadow-xs shrink-0 ring-1 ring-slate-800">
                  <ShieldCheck className="w-4 h-4 text-rose-400" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h1 className="font-['Playfair_Display',serif] font-black text-slate-900 text-base sm:text-lg tracking-tight truncate">
                      Panel Admin
                    </h1>
                    {isUserWeb && userWebOrderCode ? (
                      <span className="text-[10px] font-mono font-black px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200">
                        #{userWebOrderCode}
                      </span>
                    ) : (
                      <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        <span>Sincronizado</span>
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 hidden sm:block truncate font-medium">
                    {p1Name || 'Novio'} & {p2Name || 'Novia'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <button
                  id="btn-nav-back-to-dashboard"
                  type="button"
                  onClick={() => handleNavClick('dashboard')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200 text-xs font-bold transition-all active:scale-95 cursor-pointer shrink-0 shadow-xs"
                >
                  <ArrowLeft className="w-3.5 h-3.5 text-rose-500" />
                  <span className="hidden xs:inline">Inicio</span>
                </button>
                <span className="text-slate-300 font-bold hidden sm:inline">/</span>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs sm:text-sm font-bold text-slate-900 truncate">
                    {ADMIN_SECTION_TITLES[activeSection]}
                  </span>
                  <button
                    type="button"
                    onClick={() => copyRouteLink(ADMIN_SECTION_PATHS[activeSection])}
                    className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 hover:bg-slate-200 border border-slate-200 text-[10px] font-mono font-medium text-slate-700 transition-all cursor-pointer shrink-0"
                    title="Copiar ruta directa"
                  >
                    <span>{ADMIN_SECTION_PATHS[activeSection]}</span>
                    <Copy className="w-2.5 h-2.5 opacity-60" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right: Actions & Authoritative Non-Interactive Profile Badge */}
          <div className="flex items-center gap-2 sm:gap-2.5 shrink-0">
            {/* Pure Static Profile Badge (No dropdown, no chevron, no selector, no switching) */}
            <div
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold shadow-2xs select-none ${
                activeProfile === 'novio'
                  ? 'bg-blue-50/90 border-blue-200 text-blue-900 ring-2 ring-blue-100/50'
                  : activeProfile === 'novia'
                  ? 'bg-rose-50/90 border-rose-200 text-rose-900 ring-2 ring-rose-100/50'
                  : 'bg-slate-100 border-slate-200 text-slate-800'
              }`}
              title={`Sesión autenticada: ${activeProfile === 'novio' ? 'Novio' : activeProfile === 'novia' ? 'Novia' : 'Administrador'}`}
            >
              <span>{activeProfile === 'novio' ? '💙 Novio' : activeProfile === 'novia' ? '💗 Novia' : '🛡️ Administrador'}</span>
            </div>

            {/* Public Garden link */}
            <button
              id="btn-nav-view-public"
              type="button"
              onClick={() => onNavigateToPublic('garden')}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-bold text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-50 border border-slate-200 transition-all cursor-pointer shadow-2xs"
              title="Ver Web Pública"
            >
              <ExternalLink className="w-3.5 h-3.5 text-rose-500" />
              <span className="hidden md:inline">Ver Web</span>
            </button>

            {/* Logout button */}
            <button
              id="btn-nav-logout"
              type="button"
              onClick={onLogout}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-bold text-slate-600 hover:text-rose-700 bg-slate-100 hover:bg-rose-50 border border-slate-200/80 transition-all cursor-pointer shadow-2xs"
              title="Cerrar sesión de administrador"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Salir</span>
            </button>

            {/* Mobile menu trigger */}
            <button
              id="btn-nav-mobile-toggle"
              type="button"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              className="p-2 rounded-xl bg-slate-100 text-slate-700 hover:text-slate-900 border border-slate-200 cursor-pointer shadow-2xs"
              title="Abrir menú de navegación"
            >
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-2 max-h-[75vh] overflow-y-auto shadow-lg">
            <div className="text-[10px] uppercase font-bold text-slate-500 px-1 tracking-wider">
              Módulos del Panel
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              {displayedNavItems.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleNavClick(item.id)}
                    className={`flex items-center gap-2 p-2.5 rounded-xl text-xs font-bold text-left transition-all ${
                      isActive
                        ? 'bg-slate-900 text-white shadow-xs'
                        : 'bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200'
                    }`}
                  >
                    <span className={isActive ? 'text-white' : 'text-slate-500'}>{item.icon}</span>
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </header>

      {/* Floating Modern Toast */}
      {toast && (
        <div className="bg-slate-900 text-white font-medium px-4 py-2.5 text-xs flex items-center justify-between sticky top-[64px] z-30 shadow-md border-b border-slate-800">
          <div className="max-w-7xl mx-auto w-full flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 font-semibold">
              <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
              <span>{toast}</span>
            </span>
            {onCloseToast && (
              <button
                onClick={onCloseToast}
                className="p-1 rounded-lg hover:bg-white/10 cursor-pointer transition-colors"
                title="Cerrar notificación"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};
